// DEV-1902: the quiz's language tag ends up in <html lang>, which Chrome uses to
// decide whether to offer "Translate this page?". A wrong tag gets the Vue-rendered
// quiz rewritten mid-funnel, so a build must never guess: no fallback, no default.
//
// Rules: a real BCP 47 tag, with a region (pt-BR and pt-PT are different quizzes),
// in canonical spelling so the editor, the row and the bundle all agree.

export function assertQuizLang(raw: string | undefined): string {
  if (!raw) {
    throw new Error(
      'VITE_QUIZ_LANG is not set. Every quiz build needs the quiz row\'s "language" (e.g. de-DE).',
    );
  }

  const invalid = new Error(`VITE_QUIZ_LANG "${raw}" is not a valid language tag (expected e.g. de-DE).`);
  let canonical: string;
  try {
    [canonical] = Intl.getCanonicalLocales(raw);
  } catch {
    throw invalid;
  }
  // BCP 47 syntax also admits 5-8 letter language subtags, so "German" would
  // pass Intl. Real languages are 2-3 letters. "und" (undetermined) comes back
  // from V8 as an undefined language.
  const locale = new Intl.Locale(canonical);
  const language = locale.language ?? '';
  if (language.length < 2 || language.length > 3 || language === 'und') throw invalid;

  if (!locale.region) {
    throw new Error(`VITE_QUIZ_LANG "${raw}" needs a region (e.g. de-DE, pt-BR), not just a language.`);
  }
  if (canonical !== raw) {
    throw new Error(`VITE_QUIZ_LANG "${raw}" is not in canonical form: use "${canonical}".`);
  }
  return canonical;
}

export function isQuizLang(raw: unknown): raw is string {
  if (typeof raw !== 'string') return false;
  try {
    assertQuizLang(raw);
    return true;
  } catch {
    return false;
  }
}
